/**
 * HalfScoreForm component for entering akka/pappi counts.
 *
 * In kyykkä:
 * - Each team has 20 "torni" (towers) = 20 stacks of 2 kyykät = 40 kyykät total
 * - Referees count REMAINING kyykäs (not cleared ones)
 * - Akka = kyykkä still inside the playing area (-2 points each)
 * - Pappi = kyykkä on the edge/border (-1 point each)
 * - Score = akka × -2 + pappi × -1
 *
 * Higher score (less negative) is better. Goal is to clear the opponent's kyykät.
 */

import { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
  Paper,
  InputAdornment,
} from '@mui/material';
import { calculateDisplayScore } from '@/types';

// Score validation constants
const MAX_AKKA = 40;   // Max kyykät that can be akka (inside)
const MIN_AKKA = 0;    // Can't have negative akka
const MAX_PAPPI = 40;  // Max kyykät that can be pappi (on edge)
const MIN_PAPPI = -16; // Negative pappi = unthrown karttus (max 16 throws per half)

interface TeamScoreInputProps {
  teamName: string;
  akka: number;
  pappi: number;
  onAkkaChange: (value: number) => void;
  onPappiChange: (value: number) => void;
  disabled?: boolean;
}

function TeamScoreInput({
  teamName,
  akka,
  pappi,
  onAkkaChange,
  onPappiChange,
  disabled = false,
}: TeamScoreInputProps) {
  const displayScore = calculateDisplayScore(akka, pappi); // akka × -2 + pappi × -1

  // Local string state for input fields to handle intermediate states
  const [akkaStr, setAkkaStr] = useState(String(akka));
  const [pappiStr, setPappiStr] = useState(String(pappi));

  // Sync local state when props change (e.g., reset)
  useEffect(() => {
    setAkkaStr(String(akka));
  }, [akka]);

  useEffect(() => {
    setPappiStr(String(pappi));
  }, [pappi]);

  // Handle input change - allow any input, parse on blur
  const handleChange = (
    value: string,
    setStr: (v: string) => void,
    setNum: (v: number) => void
  ) => {
    setStr(value);
    // Try to parse immediately for responsive feedback
    const num = parseInt(value, 10);
    if (!isNaN(num)) {
      setNum(num);
    }
  };

  // Handle blur - finalize and clamp value
  const handleBlur = (
    strValue: string,
    setStr: (v: string) => void,
    setNum: (v: number) => void,
    min: number,
    max: number
  ) => {
    const num = parseInt(strValue, 10);
    if (isNaN(num)) {
      setStr('0');
      setNum(0);
    } else {
      // Clamp to valid range [min, max]
      const clamped = Math.max(min, Math.min(max, num));
      setStr(String(clamped));
      setNum(clamped);
    }
  };

  return (
    <Paper
      elevation={1}
      sx={{
        p: 2,
        mb: 2,
        bgcolor: 'background.default',
      }}
    >
      <Typography variant="h6" gutterBottom fontWeight={600}>
        {teamName}
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          label="Akkaa"
          type="text"
          inputMode="text"
          value={akkaStr}
          onChange={(e) => handleChange(e.target.value, setAkkaStr, onAkkaChange)}
          onBlur={() => handleBlur(akkaStr, setAkkaStr, onAkkaChange, MIN_AKKA, MAX_AKKA)}
          disabled={disabled}
          fullWidth
          InputProps={{
            endAdornment: <InputAdornment position="end">×-2</InputAdornment>,
            inputProps: { style: { fontSize: '1.5rem' } },
          }}
          sx={{ '& input': { textAlign: 'center' } }}
        />
        <TextField
          label="Pappia"
          type="text"
          inputMode="text"
          value={pappiStr}
          onChange={(e) => handleChange(e.target.value, setPappiStr, onPappiChange)}
          onBlur={() => handleBlur(pappiStr, setPappiStr, onPappiChange, MIN_PAPPI, MAX_PAPPI)}
          disabled={disabled}
          fullWidth
          InputProps={{
            endAdornment: <InputAdornment position="end">×-1</InputAdornment>,
            inputProps: { style: { fontSize: '1.5rem' } },
          }}
          sx={{ '& input': { textAlign: 'center' } }}
        />
      </Box>

      <Typography
        variant="h4"
        fontWeight={700}
        color={displayScore > -40 ? 'success.main' : displayScore < -60 ? 'error.main' : 'text.primary'}
        textAlign="center"
      >
        {displayScore}
      </Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center">
        {akka} × -2 + {pappi} × -1 = {displayScore}
      </Typography>
    </Paper>
  );
}

interface HalfScoreFormProps {
  team1Name: string;
  team2Name: string;
  initialTeam1?: { akka: number; pappi: number };
  initialTeam2?: { akka: number; pappi: number };
  disabled?: boolean;
  onScoreChange?: (team1: { akka: number; pappi: number }, team2: { akka: number; pappi: number }) => void;
}

export function HalfScoreForm({
  team1Name,
  team2Name,
  initialTeam1 = { akka: 0, pappi: 0 },
  initialTeam2 = { akka: 0, pappi: 0 },
  disabled = false,
  onScoreChange,
}: HalfScoreFormProps) {
  const [team1Akka, setTeam1Akka] = useState(initialTeam1.akka);
  const [team1Pappi, setTeam1Pappi] = useState(initialTeam1.pappi);
  const [team2Akka, setTeam2Akka] = useState(initialTeam2.akka);
  const [team2Pappi, setTeam2Pappi] = useState(initialTeam2.pappi);

  // Notify parent of score changes
  useEffect(() => {
    onScoreChange?.(
      { akka: team1Akka, pappi: team1Pappi },
      { akka: team2Akka, pappi: team2Pappi }
    );
  }, [team1Akka, team1Pappi, team2Akka, team2Pappi, onScoreChange]);

  return (
    <Box>
      <TeamScoreInput
        teamName={team1Name}
        akka={team1Akka}
        pappi={team1Pappi}
        onAkkaChange={setTeam1Akka}
        onPappiChange={setTeam1Pappi}
        disabled={disabled}
      />

      <TeamScoreInput
        teamName={team2Name}
        akka={team2Akka}
        pappi={team2Pappi}
        onAkkaChange={setTeam2Akka}
        onPappiChange={setTeam2Pappi}
        disabled={disabled}
      />

      <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 2 }}>
        <strong>Akka</strong> on pelineliön sisään tai pelineliöiden väliin keskialueelle jäänyt kyykkä.
        <br />
        <strong>Pappi</strong> on pelineliön sivu- tai takarajalle jäänyt kyykkä.
      </Typography>
    </Box>
  );
}

export default HalfScoreForm;

